import React, { useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { FaMapMarkerAlt, FaSpinner } from 'react-icons/fa';

// Google's Autocomplete dropdown (.pac-container) is appended to document.body,
// outside any modal DOM. Modal overlays use z-index 1100, so without this rule the
// suggestions render behind the modal and can't be clicked/selected.
const PacContainerGlobalStyle = createGlobalStyle`
  .pac-container {
    z-index: 12000 !important; /* above Modal overlay (z-index 1100) */
  }
`;

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: ${(props) => props.theme.spacing(1)};

  svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1rem;
    color: ${(props) => props.theme.colors.inputGray};
  }

  label {
    position: absolute;
    top: 10%;
    left: 40px;
    transform: translateY(-50%);
    background-color: ${(props) => props.theme.colors.surface};
    color: ${(props) => props.theme.colors.inputGray};
    font-size: 14px;
    transition: all 0.2s ease;
    padding: 0 5px;
    pointer-events: none;
  }

  input {
    width: 100%;
    padding: 16px;
    padding-left: 40px;
    margin: 4px 0;
    font-size: 14px;
    border: 1px solid ${(props) => props.theme.colors.inputGray};
    border-radius: 8px;
    background-color: ${(props) => props.theme.colors.surface};
    color: ${(props) => props.theme.colors.textPrimary};
    outline: none;
    transition: border-color 0.2s ease;
    box-sizing: border-box;

    &:focus {
      border-color: ${(props) => props.theme.colors.textPrimary};
    }

    &:focus + label,
    &:not(:placeholder-shown) + label {
      color: ${(props) => props.theme.colors.textPrimary};
      font-size: 14px;
    }

    &::placeholder {
      color: transparent;
    }

    @media (max-width: 768px) {
      padding: 14px 12px 14px 36px;
      font-size: 16px; /* Prevents iOS Safari auto-zoom on focus */
      border-radius: 12px;
      min-height: 48px;
      margin: 2px 0;

      &::placeholder {
        color: ${(props) => props.theme.colors.inputGray};
      }
    }
  }

  @media (max-width: 768px) {
    margin-bottom: 6px;

    svg {
      left: 12px;
      font-size: 0.9rem;
    }
    label {
      display: none;
    }
  }
`;

const SpinnerWrapper = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: ${(props) => props.theme.colors.primary};
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: translateY(-50%) rotate(0deg); }
    100% { transform: translateY(-50%) rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: ${(props) => props.theme.colors.error};
  font-size: 12px;
  margin-top: 5px;
`;


const GooglePlaceAutocomplete = ({ onSelect, initialValue = '', ...props }) => {
  const autocompleteRef = useRef(null);
  const [inputValue, setInputValue] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);

  useEffect(() => {
    // Update the input value when initialValue changes
    setInputValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    // Check if Google Maps script is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      setGoogleScriptLoaded(true);
      setIsLoading(false);
      initializeAutocomplete();
      return;
    }

    // If not loaded, check if it's already being loaded by another instance
    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    
    if (existingScript) {
      // If script is already being loaded, wait for it to load
      existingScript.addEventListener('load', () => {
        setGoogleScriptLoaded(true);
        setIsLoading(false);
        initializeAutocomplete();
      });
      
      // Also handle script load errors
      existingScript.addEventListener('error', () => {
        setError('Failed to load Google Maps API');
        setIsLoading(false);
      });
    } else {
      // If no script is loading, add it to the page
      try {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        
        if (!apiKey) {
          setError('Google Maps API key is missing');
          setIsLoading(false);
          return;
        }
        
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        
        script.onload = () => {
          setGoogleScriptLoaded(true);
          setIsLoading(false);
          initializeAutocomplete();
        };
        
        script.onerror = () => {
          setError('Failed to load Google Maps API');
          setIsLoading(false);
        };
        
        document.body.appendChild(script);
      } catch (err) {
        setError('Error initializing Google Maps: ' + err.message);
        setIsLoading(false);
      }
    }

    // Clean up
    return () => {
      // Remove any event listeners if needed
    };
  }, []);

  const initializeAutocomplete = () => {

    if (!window.google || !window.google.maps || !window.google.maps.places || !autocompleteRef.current) {
      return;
    }

    try {
      const autocomplete = new window.google.maps.places.Autocomplete(autocompleteRef.current, {
        types: ['geocode'],
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          const address = place.formatted_address;
          const latitude = place.geometry.location.lat();
          const longitude = place.geometry.location.lng();
          
          // Set the selected address in the input field
          setInputValue(address);
          
          // Call the parent function to pass the address, lat, long, and place
          onSelect(address, latitude, longitude, place);
        }
      });
    }catch (error) {
      setError('Error initializing autocomplete: ' + error.message);
      console.log("Error initializing autocomplete: ", error.message);
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  return (
    <InputWrapper>
      <PacContainerGlobalStyle />
      <FaMapMarkerAlt />
      <input
        ref={autocompleteRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        disabled={isLoading || props.disabled}
        {...props}
      />
      <label>Address</label>

      {isLoading && (
        <SpinnerWrapper>
          <FaSpinner size={16} />
        </SpinnerWrapper>
      )}
      
      {error && <ErrorMessage>{error}</ErrorMessage>}

    </InputWrapper>
  );
};

export default GooglePlaceAutocomplete;
